import * as ReactDOM from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { AppProviders } from '@bmwe/ui/providers';
import '@bmwe/ui/global';
import { routeTree } from './routeTree.gen';
import { loadConfig } from '@bmwe/ui/core';

import './styles.css';
import 'primereact/resources/themes/fluent-light/theme.css';
import 'primeicons/primeicons.css';
import { ForbiddenError } from '@bmwe/data';
import {
  AuthProvider,
  ErrorFallback,
  ForbiddenFallback,
  LoadingIndicator,
  useAuth,
} from '@bmwe/ui';

const KWG_REQUIRED_GROUPS = [
  'POEM Bearbeiter Kriegswaffen',
  'POEM Gaeste KWG',
  'POEM Admin KWG',
];

function DefaultErrorComponent({ error }: { error: Error }) {
  if (error instanceof ForbiddenError) {
    return <ForbiddenFallback requiredGroups={KWG_REQUIRED_GROUPS} />;
  }

  return <ErrorFallback error={error} />;
}

const router = createRouter({
  routeTree,
  context: {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: DefaultErrorComponent,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

function InnerApp() {
  const auth = useAuth();

  if (auth.isForbidden) {
    return <ForbiddenFallback requiredGroups={KWG_REQUIRED_GROUPS} />;
  }

  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <div
        style={{
          display: 'grid',
          height: '100dvh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'grid',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <LoadingIndicator />
          <div>Anwendung wird geladen...</div>
        </div>
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth }} />;
}

// Load configuration before rendering the app
loadConfig()
  .then(() => {
    root.render(
      <AppProviders>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </AppProviders>,
    );
  })
  .catch((error) => {
    console.error('Failed to load configuration:', error);
    // Still render the app even if config fails to load
    root.render(
      <AppProviders>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </AppProviders>,
    );
  });
